import { request } from "./api";

export interface ManualControlReturnPreview {
  request_id?: string | null;
  recovery_active?: boolean;
  return_token: string;
  manual_generation: string | number;
  queue_revision: string | number;
  can_return: boolean;
  pending_reconciliation?: boolean;
  terminal?: boolean;
  needs_new_preview?: boolean;
  reason?: string | null;
  [key: string]: unknown;
}

export interface ManualControlReturnResult {
  request_id: string;
  released: boolean;
  resume_started: boolean;
  pending_reconciliation: boolean;
  terminal?: boolean;
  needs_new_preview?: boolean;
  reason?: string | null;
  [key: string]: unknown;
}

export interface ManualReturnState {
  local_stopped?: boolean;
  manual_control?: boolean;
  safety_hold?: { active?: boolean } | null;
}

export interface ManualControlReturnAttempt {
  deviceId: string;
  preview: ManualControlReturnPreview;
  requestId: string;
}

const attemptKey=(deviceId:string)=>`manual-control-return:${deviceId}`;
export const loadManualReturnAttempt=(deviceId:string):ManualControlReturnAttempt|null=>{
  try{
    const value=JSON.parse(sessionStorage.getItem(attemptKey(deviceId))||"null") as ManualControlReturnAttempt|null;
    return value?.deviceId===deviceId&&value.preview?.return_token&&value.requestId?value:null;
  }catch{return null;}
};
export const saveManualReturnAttempt=(value:ManualControlReturnAttempt|null,deviceId:string)=>{
  try{
    if(value)sessionStorage.setItem(attemptKey(deviceId),JSON.stringify(value));
    else sessionStorage.removeItem(attemptKey(deviceId));
  }catch{/* The in-memory lock remains active when browser storage is unavailable. */}
};
export const hasPendingManualReturnAttempt=(deviceId:string)=>Boolean(loadManualReturnAttempt(deviceId));

export const requiresManualReturn = (device: ManualReturnState | null | undefined) =>
  Boolean(device?.local_stopped || device?.manual_control || device?.safety_hold?.active);

const reasonLabels:Record<string,string>={
  local_stop_changed:"手机收到新的本机停止操作，请重新点击继续",
  physical_release_unconfirmed:"手机仍处于人工控制，尚未确认交还",
  queue_content_changed:"队列内容已变化，请重新预览后交还",
  manual_generation_changed:"人工控制状态已变化，请重新预览",
  return_token_invalid:"交还凭据无效，请重新预览",
  return_token_expired:"交还凭据已过期，请重新预览",
  request_in_progress:"交还请求仍在处理中，请使用原请求核对",
  pending_reconciliation:"交还结果正在核对，期间不会重复执行原动作",
  nothing_to_return:"当前没有需要交还的人工控制",
  queue_pause_required:"系统将先安全暂停队列，再交还并继续",
  execution_reconciliation_required:"执行记录仍在核对，请使用原请求继续核对",
  safety_revision_changed:"手机安全状态已变化，请重新预览",
  resume_unconfirmed:"队列继续结果尚未确认，请使用原请求核对",
  return_outcome_unknown:"交还结果尚未确认，请使用原请求核对",
  CANCEL_NOT_REQUESTED:"任务已暂停，可继续或终止",
  prepared:"交还请求已登记，正在核对",
  queue_paused:"手机队列已安全暂停，正在交还控制",
  candidate_closed:"输入会话已关闭，正在交还控制",
  lease_released:"人工控制已释放，正在核对队列",
  lease_not_active:"人工控制已结束，正在核对队列",
};
export const manualReturnReasonText=(reason:unknown,fallback:string)=>{
  if(typeof reason!=="string"||!reason.trim())return fallback;
  const value=reason.trim();
  return reasonLabels[value]||(/^[a-z0-9_.-]+$/i.test(value)?fallback:value);
};

export const manualControlReturnApi = {
  preview: (deviceId: string) =>
    request<ManualControlReturnPreview>(`/devices/${encodeURIComponent(deviceId)}/manual-control/return-preview`),
  submit: (deviceId: string, body: { request_id: string; return_token: string }) =>
    request<ManualControlReturnResult>(`/devices/${encodeURIComponent(deviceId)}/manual-control/return-and-resume`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
